import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/modal/Modal';
import SvgIcon from '@/components/SvgIcon';
import useSingleQuery from '@/hook/useSingleQuery';
import type { CenterCoachEvidenceFilter, CenterCoachMetricKey } from '@/api/feedDashboard';
import { fetchGetCenterCoachMetricEvidence } from '@/services/useFeedDashboardQueryService';
import type {
    CoachMetricEvidenceItem,
    CoachMetricEvidenceResponse,
} from '@/services/useFeedDashboardQueryService';

/**
 * 지표별 강사 순위 막대 1개의 원천 데이터를 보여준다.
 *
 * 이 모달의 목적은 "75%" 를 다시 보여주는 게 아니라 "분자 15가 무엇이고 분모 20이 무엇인지"를
 * 행 단위로 나열하는 것이다. 그래서 산식 블록의 분자·분모가 곧 목록 필터 탭이고,
 * 금액·평균 지표는 각 행의 기여분(amount)과 목록 합계를 함께 보여준다.
 */

type Props = {
    centerId: string;
    coachId: string;
    coachName: string;
    metricKey: CenterCoachMetricKey;
    /** YYYY-MM */
    month: string;
    onClose: () => void;
};

const PAGE_SIZE = 20;

const formatNumber = (value: number) => value.toLocaleString('ko-KR');
type NewOpportunityJournalAction = Extract<
    NonNullable<CoachMetricEvidenceItem['journalAction']>,
    { sessionType: 'NEW_OPPORTUNITY' }
>;

export const buildNewOpportunityJournalEvidencePath = ({
    action,
    month,
    coachId,
}: {
    action: NewOpportunityJournalAction;
    month: string;
    coachId: string;
}) => {
    const monthStart = dayjs(`${month}-01`).startOf('month');
    const params = new URLSearchParams({
        startDate: monthStart.format('YYYY-MM-DD'),
        endDate: monthStart.endOf('month').format('YYYY-MM-DD'),
        coachId,
        journalEligible: 'true',
        journalEvidence: 'true',
    });
    if (action.postId) {
        params.set('journalId', action.newOpportunityId);
        if (action.displayName) params.set('journalTitle', action.displayName);
        if (action.startTime) params.set('journalStartTime', action.startTime);
    }
    return `/centerReport/newOpportunities?${params.toString()}`;
};

const formatMetricValue = (
    value: number | null,
    valueType: CoachMetricEvidenceResponse['metric']['valueType'],
    unit: string,
) => {
    if (value == null) return '-';
    switch (valueType) {
        case 'CURRENCY':
            return `${formatNumber(value)}원`;
        case 'PERCENTAGE':
            return `${value}%`;
        case 'RATING':
            return `★ ${value.toFixed(1)}`;
        default:
            return `${formatNumber(value)}${unit}`;
    }
};

const formatAmount = (
    amount: number,
    valueType: CoachMetricEvidenceResponse['metric']['valueType'],
) => {
    if (valueType === 'RATING') return `★ ${amount}`;
    return `${formatNumber(amount)}원`;
};

const CoachMetricEvidenceModal = ({ centerId, coachId, coachName, metricKey, month, onClose }: Props) => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<CenterCoachEvidenceFilter>('all');
    const [page, setPage] = useState(1);

    const { refinedResult, isLoading, isFetching, refresh } = useSingleQuery({
        fetchFn: fetchGetCenterCoachMetricEvidence,
        params: { centerId, coachId, metricKey, month, filter, page, size: PAGE_SIZE },
        enabled: !!centerId && !!coachId && !!month,
        suspense: false,
    });

    const data = refinedResult?.data as CoachMetricEvidenceResponse | undefined;
    const isError = Boolean((refinedResult as any)?.isError);

    const metric = data?.metric;
    const evidence = data?.evidence;
    const items = data?.items ?? [];
    const totalItems = data?.page.totalItems ?? 0;
    const totalPages = totalItems ? Math.ceil(totalItems / PAGE_SIZE) : 0;

    const valueType = metric?.valueType ?? 'COUNT';
    const displayValue = metric ? formatMetricValue(metric.value, metric.valueType, metric.unit) : '-';
    const headlineText = metric?.degraded ? '집계 실패' : displayValue;
    // 금액은 '17,580,730원'처럼 길어질 수 있다. 고정 폭이면 단위(원)만 다음 줄로 떨어지므로
    // 폭은 내용에 맞추고(shrink-0 + nowrap), 길이에 따라 글자 크기를 낮춰 한 줄을 유지한다.
    const headlineSizeClass =
        headlineText.length > 13 ? 'text-[26px]' : headlineText.length > 10 ? 'text-[29px]' : 'text-[32px]';

    /** 분자 < 분모 인 지표(비율)만 탭이 의미가 있다. 순수 건수 지표는 목록이 곧 전부다. */
    const hasSplit = Boolean(
        evidence && evidence.mode === 'COUNT' && evidence.listedDenominator > evidence.listedNumerator,
    );

    const tabs = useMemo(() => {
        if (!hasSplit || !data || !evidence) return [];
        return [
            { value: 'all' as CenterCoachEvidenceFilter, label: '전체', count: evidence.listedDenominator },
            {
                value: 'included' as CenterCoachEvidenceFilter,
                label: data.numeratorLabel,
                count: evidence.listedNumerator,
            },
            {
                value: 'excluded' as CenterCoachEvidenceFilter,
                // 탭 문구는 목록 행 배지와 같은 단어를 쓴다(일지 → '미작성', 재등록 → '신규').
                label: data.excludedLabel || '미포함',
                count: evidence.listedDenominator - evidence.listedNumerator,
            },
        ];
    }, [data, evidence, hasSplit]);

    const changeFilter = (next: CenterCoachEvidenceFilter) => {
        setFilter(next);
        setPage(1);
    };
    const openJournalAction = (action: NewOpportunityJournalAction) => {
        navigate(buildNewOpportunityJournalEvidencePath({ action, month, coachId }));
    };

    return (
        <Modal whiteViewWidth={1020} whiteViewHeight={800} borderRadius={20} isCloseButton onClose={onClose} isCloseOutside>
            <div className="flex h-full flex-col px-[36px] pb-[28px] pt-[32px]">
                {/* 헤더 */}
                <div className="pr-[60px]">
                    <h2 className="text-[24px] font-semibold leading-[32px] tracking-[-0.5px] text-[#252A31]">
                        {coachName || data?.coachName || '강사'} · {metric?.label ?? '지표'} 원천 데이터
                    </h2>
                    <p className="mt-[5px] text-[14px] leading-[20px] tracking-[-0.3px] text-[#7A828D]">
                        {data?.snapshot
                            ? '월 선택과 무관한 현재 시점 기준이에요.'
                            : `${dayjs(`${month}-01`).format('YYYY년 M월')} 기준이에요.`}
                        {data?.definition ? ` ${data.definition}` : ''}
                    </p>
                </div>

                {/* 산식 — 분자/분모가 그대로 목록 필터가 된다 */}
                <div className="mt-[22px] flex min-h-[104px] items-stretch rounded-[14px] border border-[#DCE4F2] bg-[#F7F9FD] px-[28px] py-[18px]">
                    <div className="flex min-w-[168px] shrink-0 flex-col justify-center border-r border-[#DDE4EF] pr-[26px]">
                        <span className="text-[13px] font-medium text-[#606B80]">{metric?.label ?? '지표'}</span>
                        <span
                            className={`mt-[3px] whitespace-nowrap font-bold leading-[40px] tabular-nums text-[#3D73DD] ${headlineSizeClass}`}
                        >
                            {headlineText}
                        </span>
                    </div>
                    <div className="flex flex-1 items-center justify-center gap-[16px] px-[24px]">
                        {isLoading || !data || !evidence ? (
                            <span className="text-[13px] text-[#8B94A3]">산정 근거를 불러오고 있어요.</span>
                        ) : evidence.mode === 'AMOUNT_SUM' ? (
                            <div className="flex flex-col items-start gap-[3px]">
                                <span className="text-[15px] font-semibold text-[#28303D]">
                                    아래 목록 {evidence.listedDenominator}건을 모두 더한 값이에요.
                                </span>
                                <span className="text-[13px] leading-[19px] text-[#7A8394]">
                                    환불·차감은 음수로 포함돼요.
                                </span>
                            </div>
                        ) : evidence.mode === 'AVERAGE' ? (
                            <>
                                <FormulaBlock
                                    label="합계"
                                    value={
                                        evidence.listedTotal == null
                                            ? '-'
                                            : valueType === 'RATING'
                                            ? `${evidence.listedTotal}점`
                                            : `${formatNumber(evidence.listedTotal)}원`
                                    }
                                />
                                <span className="text-[22px] font-light text-[#9AA3B2]">÷</span>
                                <FormulaBlock label={data.denominatorLabel} value={`${evidence.listedDenominator}건`} />
                                <span className="text-[22px] font-light text-[#9AA3B2]">=</span>
                                <span className="text-[24px] font-bold text-[#28303D]">{displayValue}</span>
                            </>
                        ) : hasSplit ? (
                            <>
                                <FormulaTab
                                    label={data.numeratorLabel}
                                    value={`${evidence.listedNumerator}`}
                                    active={filter === 'included'}
                                    onClick={() => changeFilter('included')}
                                    emphasized
                                />
                                <span className="text-[22px] font-light text-[#9AA3B2]">÷</span>
                                <FormulaTab
                                    label={data.denominatorLabel}
                                    value={`${evidence.listedDenominator}`}
                                    active={filter === 'all'}
                                    onClick={() => changeFilter('all')}
                                />
                                <span className="text-[17px] font-medium text-[#9AA3B2]">× 100</span>
                                <span className="text-[22px] font-light text-[#9AA3B2]">=</span>
                                <span className="min-w-[62px] text-[24px] font-bold text-[#28303D]">{displayValue}</span>
                            </>
                        ) : (
                            <>
                                <FormulaBlock label={data.evidenceLabel} value={`${evidence.listedDenominator}건`} emphasized />
                                <span className="text-[13px] leading-[19px] text-[#7A8394]">
                                    아래 목록이 이 숫자를 이루는 건 전부예요.
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* 집계 불일치 경고 — 숫자를 맞추려고 목록을 잘라내지 않는다 */}
                {data && !evidence?.complete && (
                    <div className="mt-[12px] flex items-start gap-[8px] rounded-[10px] border border-[#F3DFC4] bg-[#FDF8EF] px-[14px] py-[10px]">
                        <span className="mt-[1px] text-[13px]">⚠️</span>
                        <span className="text-[12px] leading-[18px] text-[#8A6A32]">
                            {metric?.degraded
                                ? '집계가 일시적으로 실패해 이 지표의 값과 근거를 신뢰할 수 없어요. 잠시 후 다시 시도해 주세요.'
                                : '목록이 위 숫자를 완전히 설명하지 못해요. 삭제된 회원·수강권처럼 지금은 조회되지 않는 원천이 포함돼 있을 수 있어요. 숫자를 맞추기 위해 목록을 임의로 조정하지는 않았어요.'}
                        </span>
                    </div>
                )}

                {/* 목록 헤더 + 탭 */}
                <div className="mt-[18px] flex items-end justify-between border-b border-[#E5E8EC]">
                    <div className="flex h-[43px] items-center gap-[24px]">
                        {tabs.length > 0 ? (
                            tabs.map((tab) => {
                                const isActive = filter === tab.value;
                                return (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        onClick={() => changeFilter(tab.value)}
                                        className={`relative flex items-start gap-[5px] self-end pb-[11px] text-[14px] font-medium transition-colors ${
                                            isActive ? 'text-[#3D73DD]' : 'text-[#767F8A] hover:text-[#3E464F]'
                                        }`}
                                    >
                                        {tab.label}
                                        <span
                                            className={`rounded-full px-[7px] py-[1px] text-[11px] leading-[17px] ${
                                                isActive ? 'bg-[#ECF1FC] text-[#3D73DD]' : 'bg-[#F2F4F6] text-[#8A929C]'
                                            }`}
                                        >
                                            {tab.count}
                                        </span>
                                        {isActive && <span className="absolute bottom-0 left-0 h-[2px] w-full bg-[#3D73DD]" />}
                                    </button>
                                );
                            })
                        ) : (
                            <span className="self-end pb-[11px] text-[14px] font-medium text-[#3E464F]">
                                {data?.evidenceLabel ?? '원천 데이터'}
                            </span>
                        )}
                    </div>
                    <span className="mb-[12px] text-[12px] text-[#8B939D]">{totalItems}건</span>
                </div>

                {/* 목록 */}
                <div className="mt-[12px] min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#E4E7EB]">
                    <div className="relative h-full overflow-y-auto bg-white">
                        {isLoading ? (
                            <CenteredState text="원천 데이터를 불러오고 있어요." />
                        ) : isError ? (
                            <div className="flex h-full flex-col items-center justify-center">
                                <span className="text-[14px] text-[#606975]">목록을 불러오지 못했어요.</span>
                                <button
                                    type="button"
                                    onClick={() => refresh()}
                                    className="mt-[10px] rounded-[7px] border border-[#D8DDE3] px-[14px] py-[6px] text-[12px] text-[#505963] hover:bg-[#F7F8FA]"
                                >
                                    다시 시도
                                </button>
                            </div>
                        ) : items.length === 0 ? (
                            <CenteredState text="해당하는 건이 없어요." />
                        ) : (
                            items.map((item) => (
                                <EvidenceRow
                                    key={item.id}
                                    item={item}
                                    valueType={valueType}
                                    onNewOpportunityJournalAction={openJournalAction}
                                />
                            ))
                        )}
                        {isFetching && !isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/65 text-[13px] text-[#6E7782]">
                                목록을 업데이트하고 있어요.
                            </div>
                        )}
                    </div>
                </div>

                {/* 페이지네이션 */}
                <div className="mt-[14px] flex h-[30px] items-center justify-between">
                    <span className="text-[12px] text-[#8B939D]">
                        {data?.snapshot ? '현재 시점 기준 · 월을 바꿔도 값이 같아요.' : `${dayjs(`${month}-01`).format('YYYY년 M월')} 집계 기준`}
                    </span>
                    <div className="flex items-center gap-[10px]">
                        <button
                            type="button"
                            aria-label="이전 페이지"
                            disabled={page <= 1 || isFetching}
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#DDE1E6] disabled:cursor-default disabled:opacity-35"
                        >
                            <SvgIcon name="SvgLeftStrokeArrow" size={12} stroke="#59616B" fill="transparent" />
                        </button>
                        <span className="min-w-[48px] text-center text-[12px] text-[#5D6570]">
                            {totalPages > 0 ? page : 0} / {totalPages}
                        </span>
                        <button
                            type="button"
                            aria-label="다음 페이지"
                            disabled={page >= totalPages || isFetching}
                            onClick={() => setPage((current) => current + 1)}
                            className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#DDE1E6] disabled:cursor-default disabled:opacity-35"
                        >
                            <SvgIcon name="SvgRightStrokeArrow" size={12} stroke="#59616B" fill="transparent" />
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const FormulaBlock = ({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) => (
    <div className="flex min-w-[84px] flex-col items-center">
        <span className="text-[12px] text-[#6D7889]">{label}</span>
        <span className={`mt-[3px] text-[21px] font-bold ${emphasized ? 'text-[#3D73DD]' : 'text-[#2E3540]'}`}>{value}</span>
    </div>
);

const FormulaTab = ({
    label,
    value,
    active,
    onClick,
    emphasized = false,
}: {
    label: string;
    value: string;
    active: boolean;
    onClick: () => void;
    emphasized?: boolean;
}) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex min-w-[92px] flex-col items-center rounded-[9px] border px-[10px] py-[6px] transition-colors ${
            active ? 'border-[#B9CDF2] bg-white' : 'border-transparent hover:bg-white/70'
        }`}
    >
        <span className="text-[12px] text-[#6D7889]">{label}</span>
        <span className={`mt-[2px] text-[21px] font-bold ${emphasized ? 'text-[#3D73DD]' : 'text-[#2E3540]'}`}>{value}</span>
    </button>
);

const CenteredState = ({ text }: { text: string }) => (
    <div className="flex h-full items-center justify-center text-[14px] text-[#7B848F]">{text}</div>
);

const EvidenceRow = ({
    item,
    valueType,
    onNewOpportunityJournalAction,
}: {
    item: CoachMetricEvidenceItem;
    valueType: CoachMetricEvidenceResponse['metric']['valueType'];
    onNewOpportunityJournalAction: (action: NewOpportunityJournalAction) => void;
}) => (
    <div className="flex min-h-[62px] items-center gap-[16px] border-b border-[#EDF0F2] px-[20px] py-[10px] last:border-b-0">
        <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-[#313840]">{item.title}</p>
            <p className="mt-[3px] truncate text-[11px] text-[#949BA4]">{item.description}</p>
        </div>
        {item.amount !== undefined && (
            <span
                className={`shrink-0 text-[13px] font-semibold tabular-nums ${
                    item.amount < 0 ? 'text-[#E0574B]' : 'text-[#333A43]'
                }`}
            >
                {formatAmount(item.amount, valueType)}
            </span>
        )}
        <span
            className={`shrink-0 rounded-full border px-[9px] py-[3px] text-[11px] font-medium ${
                item.includedInNumerator
                    ? 'border-[#C6DBF7] bg-[#EDF3FD] text-[#2C5FBF]'
                    : 'border-[#E4E7EC] bg-[#F5F6F8] text-[#69717D]'
            }`}
        >
            {item.statusLabel}
        </span>
        {item.journalAction?.sessionType === 'NEW_OPPORTUNITY' && (
            <button
                type="button"
                onClick={() => onNewOpportunityJournalAction(item.journalAction as NewOpportunityJournalAction)}
                aria-label={
                    item.journalAction.postId
                        ? `${item.journalAction.displayName || '신규기회'} 일지 읽기 전용으로 보기`
                        : `${item.journalAction.displayName || '신규기회'} 관련 신규기회 목록 보기`
                }
                className="h-[32px] shrink-0 rounded-[7px] border border-[#B9CDF2] bg-[#F5F8FE] px-[10px] text-[11px] font-semibold text-[#315FAF] hover:bg-[#EDF3FD]"
            >
                {item.journalAction.postId ? '일지 보기' : '신규기회 보기'}
            </button>
        )}
    </div>
);

export default CoachMetricEvidenceModal;
